import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../../exam.js';
import type { StepType } from '../../../extraction-progress.js';
import { bbxKind } from '../bbx-kind.js';
import { Submission } from '../../../submission.js';
import { TaskType } from '../../task-types/task-main.js';
import { runBoundingBoxAgent } from './bounding-box.js';
import type { ExtractionRegion } from './extraction-regions.js';
import { mapParsedTasksToRegions } from './extraction-regions.js';
import { createSubmissionTasks } from './submission-task.js';

interface MinimalTaskNode {
  taskNumber?: string;
  taskType?: string;
  subtasks?: MinimalTaskNode[];
}

function buildTaskTypeLookupFromMinimalTasks(minimalTasksJson: string): Map<string, string> {
  const lookup = new Map<string, string>();

  if (!minimalTasksJson) return lookup;

  try {
    const parsed = JSON.parse(minimalTasksJson);
    const tasks = Array.isArray(parsed?.tasks) ? (parsed.tasks as MinimalTaskNode[]) : [];

    const walk = (nodes: MinimalTaskNode[], path: string[]) => {
      for (const node of nodes) {
        const taskNumber = String(node?.taskNumber ?? '').trim();
        if (!taskNumber) continue;

        const currentPath = [...path, taskNumber];
        const currentType = String(node?.taskType ?? '').trim();
        lookup.set(currentPath.join('|'), currentType || TaskType.DEFAULT);

        if (Array.isArray(node?.subtasks) && node.subtasks.length > 0) {
          walk(node.subtasks, currentPath);
        }
      }
    };

    walk(tasks, []);
  } catch (error) {
    logger.warn(error, 'Failed to parse minimal task structure while building task type lookup');
  }

  return lookup;
}

function enrichRegionsWithTaskTypes(regions: ExtractionRegion[], minimalTasksJson: string): ExtractionRegion[] {
  const taskTypeLookup = buildTaskTypeLookupFromMinimalTasks(minimalTasksJson);

  return regions.map(region => {
    const path = Array.isArray(region.taskPath)
      ? region.taskPath.map(part => String(part ?? '').trim()).filter(Boolean)
      : [];
    const taskType = taskTypeLookup.get(path.join('|')) || region.taskType || TaskType.DEFAULT;

    return {
      ...region,
      taskType,
    };
  });
}

export async function processSubmissionById(
  submissionId: string,
  minimalTasksJsonLocal: string,
  addCostLocal: (cost: unknown) => void,
  examID?: string,
  step?: StepType,
): Promise<boolean> {
  let submissionFailed = false;
  try {
    const submissionCollection = await getCollection(Submission as any);
    const submission = await submissionCollection.findOne({ _id: submissionId } as any);
    if (!submission) {
      logger.warn({ submissionId }, 'Submission not found');
      submissionFailed = true;
      return false;
    }

    if (!submission.imageIds || submission.imageIds.length === 0) {
      logger.warn({ submissionId }, 'Submission has no imageIds, skipping');
      submissionFailed = true;
      return false;
    }

    // ANSWERED ONLINE — there is nothing to extract, and extracting would DESTROY the answers: this
    // function ends in an unconditional `$set: { taskSubmissions }`, and the pages it would read are the
    // exam's blank printed pages (see `answersSubmittedOnline` on Submission). Not a failure: the
    // submission already holds exactly what extraction exists to produce.
    if (submission.answersSubmittedOnline) {
      logger.info({ submissionId }, 'Submission was answered online — its answers are already structured');
      return true;
    }

    const boundingBoxResult = await runBoundingBoxAgent(
      submission.imageIds,
      bbxKind.STUDENT_ANSWER,
      minimalTasksJsonLocal,
    );
    addCostLocal(boundingBoxResult?.cost);

    if (!boundingBoxResult) {
      submissionFailed = true;
      return false;
    }

    let regions: ExtractionRegion[] = [];
    try {
      const rawAnswer = typeof boundingBoxResult === 'string' ? boundingBoxResult : boundingBoxResult?.answer;
      if (rawAnswer) {
        const parsed = typeof rawAnswer === 'string' ? JSON.parse(rawAnswer) : rawAnswer;
        regions = enrichRegionsWithTaskTypes(mapParsedTasksToRegions(parsed), minimalTasksJsonLocal);
      }
    } catch (e) {
      logger.error({ err: e, submissionId }, 'BBX parsing error for submission');
      submissionFailed = true;
      regions = [];
    }

    let submissionTasks: any[] = [];
    try {
      const submissions = await getCollection(Submission);
      const submission = (await submissions.findOne({ _id: submissionId })) as Submission;

      // Fetch exam to get all expected tasks
      let examTasks: any[] = [];
      if (examID) {
        const examCollection = await getCollection(Exam as any);
        const exam = await examCollection.findOne({ _id: examID } as any);
        if (exam && Array.isArray(exam.tasks)) {
          examTasks = exam.tasks;
        }
      }

      submissionTasks = await createSubmissionTasks(regions, submission, examTasks, addCostLocal, examID, step);
    } catch (e) {
      logger.error({ err: e, submissionId }, 'Create submission tasks error');
      submissionFailed = true;
      submissionTasks = [];
    }

    try {
      const plainSubmissionTasks = submissionTasks.map((task: any) => JSON.parse(JSON.stringify(task)));
      await submissionCollection.updateOne({ _id: submissionId } as any, {
        $set: { taskSubmissions: plainSubmissionTasks },
      });
    } catch (e) {
      logger.error({ err: e, submissionId }, 'DB update error for submission');
      submissionFailed = true;
    }
  } catch (e) {
    logger.error({ err: e, submissionId }, 'Unexpected error for submission');
    submissionFailed = true;
  }

  if (!submissionFailed) {
    logger.info({ submissionId }, 'Submission processed successfully');
  }
  return !submissionFailed;
}
